// Cards that fold (brief 1E-25; every record from brief 1E-32, DECISIONS 11/01 "Records that fit
// on one screen", mockup v90). Pure: what a folded card's one line says, and which cards start
// open until the person chooses.

export interface CardParts {
  /** Rows in the card's table. */
  rows: number;
  /** The card's chips, in order (the heading's first). */
  chips: readonly string[];
  /** The card's text below its heading, on one line. */
  firstLine: string;
  /** The hint under the heading: never repeated as the summary. */
  hint: string;
}

const clip = (s: string, n = 60) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * The one line under a folded card's heading: how many items and its first two chips
 * ("18 items · 1 due"), or a chip alone, or the first line of what it holds. Never the hint.
 */
export function foldSummary(p: CardParts): string {
  const chips = [...new Set(p.chips.map((c) => c.trim()).filter(Boolean))].slice(0, 2);
  const count = p.rows ? `${p.rows} ${p.rows === 1 ? "item" : "items"}` : "";
  const line = [count, ...chips].filter(Boolean).join(" · ");
  if (line) return line;
  let text = p.firstLine.replace(/\s+/g, " ").trim();
  const hint = p.hint.replace(/\s+/g, " ").trim();
  if (hint && text.startsWith(hint)) text = text.slice(hint.length).trim();
  if (!text || text === hint) return "";
  return clip(text);
}

/**
 * Whether a card starts open before the person chooses: with no list every card does; with one,
 * only the cards whose heading starts with a name on it (a caregiver: "Ready to work").
 */
export function startsOpen(label: string, openFirst?: readonly string[]): boolean {
  if (!openFirst) return true;
  return openFirst.some((n) => label.startsWith(n));
}

/** How many of a thread's newest messages show before "Show n earlier". */
export const THREAD_NEWEST = 1;

/**
 * A thread as it shows (brief 1E-32): newest first; until "Show n earlier" is clicked, only the
 * newest message, with how many earlier ones are folded under it. Takes messages oldest first.
 */
export function threadShown<T>(
  oldestFirst: readonly T[],
  all: boolean,
): { shown: T[]; earlier: number } {
  const newest = [...oldestFirst].reverse();
  if (all || newest.length <= THREAD_NEWEST) return { shown: newest, earlier: 0 };
  return { shown: newest.slice(0, THREAD_NEWEST), earlier: newest.length - THREAD_NEWEST };
}

/** The button under the newest message: "Show 12 earlier". */
export const earlierLabel = (n: number) => `Show ${n} earlier`;
